// Client-side subdomain helpers for signup + settings. These mirror the backend
// rules (tenant.NormalizeAndValidateSubdomain) to give instant feedback; the
// server remains the source of truth (uniqueness + final validation).
//
// The reserved-slug list is SHARED across all tenant products on the backend
// (backend/internal/tenant/subdomain.go). This client copy mirrors that full
// superset so the business signup form rejects the same slugs the server would.

// Kept in sync with reservedSubdomains in backend/internal/tenant/subdomain.go.
export const RESERVED_SUBDOMAINS = new Set([
  // Infra / operational
  "www", "api", "app", "admin", "mail", "smtp", "imap", "pop", "cdn", "static",
  "assets", "media", "img", "images", "prepxai", "dashboard", "portal",
  "console", "login", "signup", "auth", "account", "status", "support", "help",
  "docs", "blog", "ftp", "ns", "ns1", "ns2", "test", "staging", "stage", "dev",
  "demo", "internal", "root", "billing", "pay", "payments", "webhook", "webhooks",
  // Institution product
  "institution", "institutions",
  // Business / hiring product
  "business", "businesses", "company", "companies", "employer", "employers",
  "hire", "hiring", "job", "jobs", "career", "careers", "recruit", "recruiter",
  "recruiting",
]);

const SUBDOMAIN_RE = /^[a-z0-9]([a-z0-9-]{1,30}[a-z0-9])$/;

// sanitizeSubdomainInput is a gentle, keystroke-friendly transform: lowercase and
// drop characters that can never be valid, while still allowing a trailing hyphen
// mid-typing. It does NOT trim leading/trailing hyphens (that would fight the
// user); validateSubdomain does the strict check on submit.
export function sanitizeSubdomainInput(raw) {
  return (raw || "").toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 32);
}

// slugifySubdomain derives a valid provisional slug from free text (e.g. a
// company name) for the "suggest" affordance.
export function slugifySubdomain(raw) {
  return (raw || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32)
    .replace(/-+$/g, "");
}

// validateSubdomain returns "" when valid, else a user-facing error message.
export function validateSubdomain(raw) {
  const s = (raw || "").toLowerCase().trim();
  if (!s) return "Please choose a subdomain.";
  if (s.length < 3 || s.length > 32) return "Use between 3 and 32 characters.";
  if (s.includes("--")) return "No consecutive hyphens.";
  if (!SUBDOMAIN_RE.test(s))
    return "Use lowercase letters, numbers and single hyphens (no leading or trailing hyphen).";
  if (RESERVED_SUBDOMAINS.has(s)) return "That subdomain is reserved. Please choose another.";
  return "";
}
